import { IconButton, TextField } from "@mui/material";
import {
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MaterialReactTable,
  getColumnId,
  useMaterialReactTable,
} from "material-react-table";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useSysStatserviceWpcQuery } from "@/api/hooks/sys-statservice-hooks";
import { BaseAutocomplete } from "@/components/a-autocomplete";
import { CustomShowHideColumnsPopover } from "@/components/material-react-table/custom-show-hide-columns-popover";
import { useTablePersistentSettings, useTableSettings } from "@/hooks/use-table-settings";
import { statServiceRoomVariants } from "@/module/service-configs-module/statservice-module/statservice-module";
import { useStatserviceTableWpcColumns } from "@/module/service-configs-module/statservice-module/statservice-table-wpc-columns";
import { ResultStatsRedisDto } from "@/shared/api";

const StatserviceTableWpc = ({
  setRowsAmount,
  selectedRoom,
  setSelectedRoom,
  period,
}: {
  setRowsAmount: (amount: number) => void;
  selectedRoom: string | null;
  setSelectedRoom: (value: string | null) => void;
  period: number;
}) => {
  const { t } = useTranslation();

  const {
    data: dataRs,
    isError: isDataLoadingError,
    isLoading: dataIsLoading,
    refetch: refetchData,
    isRefetching: isDataRefetching,
  } = useSysStatserviceWpcQuery(
    { room: selectedRoom ?? "", period },
    {
      enabled: true,
    }
  );

  useEffect(() => {
    setRowsAmount(dataRs?.data.length ?? 0);
  }, [dataRs, setRowsAmount]);

  ////////Table state persistence
  const { defaultProps, state, columnFilters, grouping, globalFilter } =
    useTableSettings<ResultStatsRedisDto>("statServiceWpc");

  const columns = useStatserviceTableWpcColumns();
  const defaultColumnVisibility = useMemo(
    () => Object.fromEntries(columns.map((val) => [getColumnId(val), !!val.visible])),
    [columns]
  );
  const {
    state: persistentTableState,
    onColumnVisibilityChange,
    onColumnOrderChange,
  } = useTablePersistentSettings({
    tableName: "statServiceWpc",
    initialColumnVisibilityState: defaultColumnVisibility,
  });

  ////////////////////////////

  const table = useMaterialReactTable({
    ...defaultProps,
    data: dataRs?.data ?? [],
    columns: columns,
    selectAllMode: "all",
    enableSelectAll: true,
    enableRowSelection: true,
    enableGrouping: true,
    enableColumnDragging: true,
    enableColumnOrdering: true,
    enableColumnFilterModes: true,
    enableRowActions: true,
    enablePagination: true,
    enableSorting: true,
    state: {
      ...state,
      ...persistentTableState,
      isLoading: dataIsLoading || isDataRefetching,
    },
    onColumnVisibilityChange: (updater) => {
      onColumnVisibilityChange(updater);
      refetchData();
    },
    onColumnOrderChange: onColumnOrderChange,
    renderTopToolbarCustomActions: () => {
      return (
        <div style={{ height: "40px", display: "flex", alignItems: "center" }}>
          <BaseAutocomplete
            onChange={(_event, newValue) => {
              setSelectedRoom(newValue);
            }}
            value={selectedRoom}
            options={statServiceRoomVariants}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                style={{ width: 200 }}
                label={t("selectedRoom")}
                variant="outlined"
              />
            )}
          />
        </div>
      );
    },
    renderToolbarInternalActions: ({ table }) => (
      <>
        <MRT_ToggleFiltersButton table={table} />
        <MRT_ToggleDensePaddingButton table={table} />
        <MRT_ToggleFullScreenButton table={table} />

        <CustomShowHideColumnsPopover table={table} />
      </>
    ),
  });

  useEffect(() => {
    table.resetRowSelection();
  }, [columnFilters, grouping, globalFilter, table]);

  return (
    <>
      <MaterialReactTable table={table} />
    </>
  );
};

export default StatserviceTableWpc;
